import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X, ArrowUpRight, CalendarCheck, Check } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Flag, LANGS } from "@/components/common/Flags";
import { BookingBar, useAnchorPassed, useIsMobile } from "@/components/booking/BookingBar";
import { useLang } from "@/lib/i18n";

export const NAV = [
  { to: "/rom", nn: "Rom", en: "Rooms", id: "rooms" },
  { to: "/mat", nn: "Mat", en: "Dining", id: "dining" },
  { to: "/arrangement", nn: "Selskap og møte", en: "Events & meetings", id: "events" },
  { to: "/praktisk", nn: "Praktisk", en: "Practical", id: "practical" },
  { to: "/om-oss", nn: "Om oss", en: "About", id: "about" },
];

export function useScrolled(threshold = 8) {
  const [scrolled, setScrolled] = useState(() => (typeof window !== "undefined" ? window.scrollY > threshold : false));
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > threshold);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, [threshold]);
  return scrolled;
}

/* ------------------------------------------------------------------ language */

/** Dropdown with round flags – used in the header */
export const LangMenu = ({ testid = "header-language-toggle", className = "", align = "end", light = false }) => {
  const { lang, setLang, tx } = useLang();
  const current = LANGS.find((l) => l.code === lang) || LANGS[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`inline-flex h-10 w-10 items-center justify-center rounded-full border transition-[border-color,background-color,transform] duration-300 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
            light
              ? "border-white/25 bg-white/10 backdrop-blur hover:bg-white/20 data-[state=open]:bg-white/20"
              : "border-border bg-card hover:border-foreground/40 hover:bg-secondary data-[state=open]:border-foreground/40"
          } ${className}`}
          aria-label={`${current.label} – ${tx("byt språk", "change language")}`}
          data-testid={testid}
        >
          <Flag code={current.code} className="h-6 w-6" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} sideOffset={10} className="w-52 rounded-2xl border-border bg-card p-1.5 shadow-pop">
        {LANGS.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onSelect={() => setLang(l.code)}
            data-testid={`${testid}-${l.code}`}
            className={`flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-[0.9375rem] focus:bg-secondary ${lang === l.code ? "font-semibold text-foreground" : "font-medium text-foreground/80"}`}
          >
            <Flag code={l.code} className="h-5 w-5" />
            <span className="flex-1">{l.label}</span>
            {lang === l.code && <Check className="h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** Inline segmented switch with flags – footer and mobile menu */
export const LangSwitch = ({ testid = "footer-language-toggle", light = false, className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`inline-flex items-center rounded-full p-1 ${light ? "bg-white/10" : "bg-secondary"} ${className}`} role="group" aria-label="Language" data-testid={testid}>
      {LANGS.map((l) => {
        const active = lang === l.code;
        return (
          <button
            key={l.code}
            type="button"
            onClick={() => setLang(l.code)}
            aria-pressed={active}
            data-testid={`${testid}-${l.code}`}
            className={`inline-flex h-9 items-center gap-2 rounded-full pl-1.5 pr-3 text-sm font-semibold transition-colors ${
              active
                ? light ? "bg-white text-foreground" : "bg-card text-foreground shadow-float"
                : light ? "text-white/75 hover:text-white" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Flag code={l.code} className="h-6 w-6" /> {l.short}
          </button>
        );
      })}
    </div>
  );
};

/** Compatibility alias – older call sites */
export const LangToggle = LangSwitch;

export const Logo = ({ className = "h-7 lg:h-8", light = false }) => (
  <img src="/images/logo.png" alt="Fitjar Fjordhotell" className={`${className} w-auto transition-[filter] duration-300 ${light ? "brightness-0 invert" : ""}`} />
);

/* ------------------------------------------------------------------ header */

/** Nav item with a sliding active pill (shared layoutId -> the pill glides between items) */
const NavItem = ({ n, light, compact }) => {
  const { tx } = useLang();
  return (
    <NavLink
      to={n.to}
      data-testid={`nav-${n.id}`}
      className={({ isActive }) =>
        `relative rounded-full font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${compact ? "px-3.5 py-2 text-sm" : "px-4 py-2 text-[0.9375rem]"} ${
          isActive
            ? light ? "text-white" : "text-foreground"
            : light ? "text-white/75 hover:text-white" : "text-foreground/60 hover:text-foreground"
        }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <motion.span
              layoutId="header-nav-pill"
              transition={{ type: "spring", stiffness: 420, damping: 38 }}
              className={`absolute inset-0 rounded-full ${light ? "bg-white/15" : "bg-secondary"}`}
              aria-hidden
            />
          )}
          <span className="relative">{tx(n.nn, n.en)}</span>
        </>
      )}
    </NavLink>
  );
};

/**
 * Site header with three states:
 *  - `overlay` + top of page: transparent, white type, sits over the page's photo hero
 *  - solid page + top: plain white bar
 *  - scrolled (any page): detaches into a compact, fully opaque floating "island" (always legible,
 *    whatever photo is behind it)
 * On pages with a search (home, rooms) the centre swaps from the navigation to a compact search
 * pill once the main search has scrolled away – the nav is then reachable from the menu button.
 */
export const Header = ({ showSearch = false, overlay = false }) => {
  const { tx } = useLang();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const scrolled = useScrolled(28);
  const isMobile = useIsMobile();
  const passed = useAnchorPassed({ offset: 72 });
  const searchVisible = showSearch && passed && !isMobile;
  const light = overlay && !scrolled;
  useEffect(() => setOpen(false), [pathname]);

  const iconBtn = `inline-flex items-center justify-center rounded-full border transition-[colors,height,width] duration-300 ${scrolled ? "h-9 w-9" : "h-10 w-10"} ${
    light ? "border-white/25 bg-white/10 text-white backdrop-blur hover:bg-white/20" : "border-border bg-card text-foreground hover:bg-secondary"
  }`;

  return (
    <header
      className={`no-print fixed inset-x-0 top-0 z-50 transition-[padding,background-color] duration-300 ease-premium ${scrolled ? "px-3 pt-2.5 lg:px-6 lg:pt-3" : ""} ${!overlay && !scrolled ? "bg-white" : ""}`}
      data-testid="site-header"
      data-state={searchVisible ? "search" : "nav"}
      data-light={light ? "true" : "false"}
      data-scrolled={scrolled ? "true" : "false"}
    >
      <div
        className={`mx-auto grid w-full grid-cols-[auto_1fr_auto] items-center gap-3 transition-[max-width,height,background-color,box-shadow,border-radius,padding] duration-300 ease-premium lg:gap-6 ${
          scrolled
            ? "h-[52px] max-w-[1200px] rounded-full bg-white px-2 shadow-island ring-hairline lg:h-[60px] lg:px-2.5"
            : `max-w-site px-5 sm:px-8 lg:px-10 ${light ? "h-20 lg:h-24" : "h-16 lg:h-[72px]"}`
        }`}
      >
        <Link to="/" className={`flex shrink-0 items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${scrolled ? "pl-3" : ""}`} data-testid="header-logo-link" aria-label="Fitjar Fjordhotell – heim">
          <Logo light={light} className={scrolled ? "h-6 lg:h-7" : "h-7 lg:h-8"} />
        </Link>

        <div className="relative hidden min-w-0 lg:block">
          <AnimatePresence initial={false} mode="wait">
            {searchVisible ? (
              <motion.div
                key="search"
                initial={{ opacity: 0, y: -10, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.97 }}
                transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
                className="mx-auto w-full max-w-[560px]"
                data-testid="sticky-booking-bar"
              >
                <BookingBar variant="compact" testid="sticky-booking-bar-inner" />
              </motion.div>
            ) : (
              <motion.nav
                key="nav"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 6 }}
                transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
                className="flex items-center justify-center gap-0.5"
                aria-label={tx("Hovudmeny", "Main menu")}
              >
                {NAV.map((n) => <NavItem key={n.to} n={n} light={light} compact={scrolled} />)}
              </motion.nav>
            )}
          </AnimatePresence>
        </div>

        <div className="flex items-center justify-end gap-2">
          <Link
            to="/mi-bestilling"
            className={`hidden rounded-full px-3.5 py-2 text-sm font-medium transition-colors duration-200 md:inline-flex ${light ? "text-white/80 hover:bg-white/10 hover:text-white" : "text-foreground/60 hover:bg-secondary hover:text-foreground"}`}
            data-testid="header-my-booking-link"
          >
            {tx("Mi bestilling", "My booking")}
          </Link>
          <LangMenu light={light} className={scrolled ? "!h-9 !w-9" : ""} />
          <Button asChild className={`transition-[height,padding] duration-300 ${scrolled ? "h-9 px-4" : "h-10 px-5"}`} data-testid="header-book-cta">
            <Link to="/bestill">{tx("Bestill", "Book")}</Link>
          </Button>
          <button
            type="button"
            className={`${iconBtn} ${searchVisible ? "" : "lg:hidden"}`}
            onClick={() => setOpen(true)}
            aria-label={tx("Opne meny", "Open menu")}
            data-testid="header-mobile-menu-button"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col bg-background p-0 sm:max-w-md [&>button]:hidden" data-testid="mobile-menu">
          <div className="flex h-16 items-center justify-between px-5">
            <Logo />
            <button type="button" onClick={() => setOpen(false)} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card text-foreground transition-colors hover:bg-secondary" aria-label={tx("Lukk", "Close")} data-testid="mobile-menu-close"><X className="h-5 w-5" /></button>
          </div>
          <SheetTitle className="sr-only">{tx("Meny", "Menu")}</SheetTitle>
          <SheetDescription className="sr-only">{tx("Navigasjon", "Navigation")}</SheetDescription>
          <nav className="flex-1 overflow-y-auto px-5 pt-2">
            <ul>
              {[{ to: "/", nn: "Heim", en: "Home", id: "home" }, ...NAV].map((n) => (
                <li key={n.to} className="border-b border-border">
                  <NavLink
                    to={n.to}
                    end={n.to === "/"}
                    data-testid={`mobile-nav-${n.id}`}
                    className={({ isActive }) => `flex items-center justify-between py-4 text-[1.625rem] font-bold tracking-[-0.02em] transition-colors ${isActive ? "text-foreground" : "text-foreground/80 hover:text-foreground"}`}
                  >
                    {tx(n.nn, n.en)} <ArrowUpRight className="h-5 w-5 text-muted-foreground" />
                  </NavLink>
                </li>
              ))}
            </ul>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <Link to="/mi-bestilling" className="flex flex-col gap-3 rounded-2xl bg-secondary p-4 transition-colors hover:bg-muted" data-testid="mobile-nav-my-booking">
                <CalendarCheck className="h-5 w-5 text-foreground" strokeWidth={1.75} />
                <span className="text-sm font-semibold text-foreground">{tx("Mi bestilling", "My booking")}</span>
              </Link>
              <a href="tel:+4753457700" className="flex flex-col gap-3 rounded-2xl bg-secondary p-4 transition-colors hover:bg-muted" data-testid="mobile-menu-phone">
                <Phone className="h-5 w-5 text-foreground" strokeWidth={1.75} />
                <span className="text-sm font-semibold text-foreground tabular">53 45 77 00</span>
              </a>
            </div>
            <div className="mt-6 flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">{tx("Språk", "Language")}</p>
              <LangSwitch testid="mobile-language-toggle" />
            </div>
          </nav>
          <div className="p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
            <Button asChild size="xl" className="w-full" data-testid="mobile-menu-book-cta"><Link to="/bestill">{tx("Bestill opphald", "Book your stay")}</Link></Button>
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
};
